import React from "react";
//#region types
import {
  DateInputProps,
  NumberInputProps,
  SearchInputProps,
  TextInputProps,
} from "models/propTypes/wintFormPropsTypes";
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { CommonConstants, FieldConstants, IconDefinitionConstants, InvoiceConstants } from "utils/constants";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { SelfInvoiceFormGeneralFieldsProps } from "models/propTypes/selfInvoicePropTypes";
//#endregion
//#region components
import { DateInput, NumberInput, SearchInput, TextInput, VerticalFormField } from "components/common/wintForms";
import { useCreateCustomerDialog, useCustomerSearch } from "components/customer";
import { useCustomerService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

const SelfInvoiceFormGeneralFields: React.FC<SelfInvoiceFormGeneralFieldsProps<InvoiceFormTypes.SelfInvoice>> = (
  props
) => {
  const { connect } = props;

  const customerService = useCustomerService();

  const [topCustomers, setTopCustomers] = React.useState<AutocompleteListItem[]>([]);

  React.useEffect(() => {
    customerService.getCustomerSuggestions().then((res: WintSuperkollAPIModel.CustomerSearchResponse[]) => {
      if (res && res.length > 0) {
        setTopCustomers(
          res.map(
            (item): AutocompleteListItem => ({
              id: item.id ?? 0,
              value: item.name ?? "",
              icon: IconDefinitionConstants.WCompany,
              group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
              data: item.orgNr,
            })
          )
        );
      } else if (topCustomers.length !== 0) setTopCustomers([]);
    });
  }, []);

  const customerSearchProps = connect("customer");
  const organizationNumberProps = connect("organizationNumber");
  const invoiceDateProps = connect("invoiceDate");
  const oCRNumberProps = connect("oCRNumber");

  const searchCustomers = useCustomerSearch();

  const { showPreviewDialog } = useCreateCustomerDialog();

  const onCustomerAdd = (name?: string): Promise<AutocompleteListItem | undefined> => {
    return new Promise((resolve) => {
      const onCustomerCreateSuccess = (customerId?: number, customerName?: string, orgNr?: string) => {
        const customerResult = { id: customerId, value: customerName ?? "", data: orgNr };
        onCustomerSelect(customerResult);
        resolve(customerResult);
      };

      const onPreviewDialogClosed = () => {
        resolve(undefined);
      };

      showPreviewDialog(name, onCustomerCreateSuccess, onPreviewDialogClosed);
    });
  };

  const onCustomerSelect = (newValue?: AutocompleteListItem) => {
    customerSearchProps.setValue(newValue, true);
    organizationNumberProps.setValue(newValue?.data?.replace("-", ""));
  };

  return (
    <>
      <VerticalFormField<SearchInputProps>
        Component={SearchInput}
        {...customerSearchProps}
        setValue={onCustomerSelect}
        icon={IconDefinitionConstants.WUser}
        label={TranslationKeys.Invoice.Customer}
        items={searchCustomers}
        suggestedItems={topCustomers}
        actions={[
          {
            id: InvoiceConstants.AddCustomerAction,
            value: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.SaveValueAsCustomer,
              undefined,
              { key: FieldConstants.ValueTemplateText }
            ),
            icon: IconDefinitionConstants.WPlus,
            onActionClicked: onCustomerAdd,
            actionSuccessMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Saved),
            hide: (options: AutocompleteListItem[], value?: string, isValueFromOptions?: boolean) =>
              !value || isValueFromOptions || options.some((op) => op.value === value),
          },
        ]}
      />
      {!UtilsHelper.isEmpty(organizationNumberProps.value) && (
        <VerticalFormField<NumberInputProps>
          Component={NumberInput}
          {...organizationNumberProps}
          icon={IconDefinitionConstants.WCompany}
          label={TranslationKeys.Invoice.OrganizationNumber}
          allowLeadingZeros
          format={CommonConstants.OrganizationNumberMask}
          disabled
        />
      )}
      <VerticalFormField<DateInputProps>
        Component={DateInput}
        icon={IconDefinitionConstants.WCalendar}
        {...invoiceDateProps}
        label={TranslationKeys.Common.InvoiceDate}
      />
      <VerticalFormField<TextInputProps>
        Component={TextInput}
        icon={IconDefinitionConstants.WHashtag}
        {...oCRNumberProps}
        label={TranslationKeys.Invoice.SelfInvoiceOCR}
        descriptionText={TranslationKeys.Invoice.SelfInvoiceOCRDescription}
        type="number"
        optional
      />
    </>
  );
};

export default SelfInvoiceFormGeneralFields;
